import { useParams, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { TrustBar } from "@/components/TrustBar";
import { Services } from "@/components/Services";
import { WhyChooseUs } from "@/components/WhyChooseUs";
import { HowItWorks } from "@/components/HowItWorks";

import { Comparison } from "@/components/Comparison";
import { Testimonials } from "@/components/Testimonials";
import { NotForEveryone } from "@/components/NotForEveryone";
import { Guarantee } from "@/components/Guarantee";
import { FAQ } from "@/components/FAQ";
import { ServiceAreaMap } from "@/components/ServiceAreaMap";
import { GiftCards } from "@/components/GiftCards";
import { ReferFriend } from "@/components/ReferFriend";

import { CleanerSpotlight } from "@/components/CleanerSpotlight";
import { FinalCTA } from "@/components/FinalCTA";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { PricingCalculator } from "@/components/PricingCalculator";
import { SEO } from "@/components/SEO";
import { InternalLinks } from "@/components/InternalLinks";
import { WeatherRecommendation } from "@/components/WeatherRecommendation";
import { NeighborhoodMap } from "@/components/NeighborhoodMap";
import { LocalSEOContent } from "@/components/LocalSEOContent";
import { ALL_LOCATIONS, LocationContext, useLocationUpdate } from "@/lib/locations";
import { useEffect, Suspense, lazy } from "react";
import { useNavigate } from "react-router-dom";
import { SLUG_TO_ES } from "@/components/Navbar";

const LazyServices = lazy(() => import("@/components/Services").then(m => ({ default: m.Services })));
const LazyWhyChooseUs = lazy(() => import("@/components/WhyChooseUs").then(m => ({ default: m.WhyChooseUs })));
const LazyHowItWorks = lazy(() => import("@/components/HowItWorks").then(m => ({ default: m.HowItWorks })));

const LazyComparison = lazy(() => import("@/components/Comparison").then(m => ({ default: m.Comparison })));
const LazyTestimonials = lazy(() => import("@/components/Testimonials").then(m => ({ default: m.Testimonials })));
const LazyNotForEveryone = lazy(() => import("@/components/NotForEveryone").then(m => ({ default: m.NotForEveryone })));
const LazyGuarantee = lazy(() => import("@/components/Guarantee").then(m => ({ default: m.Guarantee })));
const LazyFAQ = lazy(() => import("@/components/FAQ").then(m => ({ default: m.FAQ })));
const LazyServiceAreaMap = lazy(() => import("@/components/ServiceAreaMap").then(m => ({ default: m.ServiceAreaMap })));
const LazyGiftCards = lazy(() => import("@/components/GiftCards").then(m => ({ default: m.GiftCards })));
const LazyReferFriend = lazy(() => import("@/components/ReferFriend").then(m => ({ default: m.ReferFriend })));

const LazyCleanerSpotlight = lazy(() => import("@/components/CleanerSpotlight").then(m => ({ default: m.CleanerSpotlight })));
const LazyBeforeYourCleaning = lazy(() => import("@/components/BeforeYourCleaning").then(m => ({ default: m.BeforeYourCleaning })));
const LazyWhatToExpectTimeline = lazy(() => import("@/components/WhatToExpectTimeline").then(m => ({ default: m.WhatToExpectTimeline })));
const LazyFinalCTA = lazy(() => import("@/components/FinalCTA").then(m => ({ default: m.FinalCTA })));
const LazyFrequencyDiscounts = lazy(() => import("@/components/FrequencyDiscounts").then(m => ({ default: m.FrequencyDiscounts })));
const LazyPricingCalculator = lazy(() => import("@/components/PricingCalculator").then(m => ({ default: m.PricingCalculator })));
const LazyInternalLinks = lazy(() => import("@/components/InternalLinks").then(m => ({ default: m.InternalLinks })));
const LazyTimeSavedCalculator = lazy(() => import("@/components/TimeSavedCalculator").then(m => ({ default: m.TimeSavedCalculator })));
const LazyChecklistVisualizer = lazy(() => import("@/components/ChecklistVisualizer").then(m => ({ default: m.ChecklistVisualizer })));
const LazyBeforeAfterGame = lazy(() => import("@/components/BeforeAfterGame").then(m => ({ default: m.BeforeAfterGame })));

const LocationPage = () => {
  const { city } = useParams<{ city: string }>();
  const navigate = useNavigate();
  const setGlobalLocation = useLocationUpdate();
  
  const locationData = ALL_LOCATIONS.find(loc => loc.slug === city);

  useEffect(() => {
    if (locationData) {
      setGlobalLocation(locationData);
      if (locationData.type === 'region') {
        localStorage.setItem("lastRegion", locationData.regionSlug);
      }
    }
    
    const pref = localStorage.getItem("langPref");
    if (pref === "es" && city && SLUG_TO_ES[city]) {
      navigate(SLUG_TO_ES[city], { replace: true });
    } else if (pref === "ru" && city) {
      navigate(`/ru/${city}`, { replace: true });
    } else if (pref === "zh" && city) {
      navigate(`/zh/${city}`, { replace: true });
    }
  }, [navigate, city, locationData]);

  if (!locationData) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <LocationContext.Provider value={locationData}>
      <div className="flex min-h-screen flex-col">
        <SEO 
          title={locationData.slug === 'sacramento' || locationData.slug === 'sacramento-area' ? "House Cleaning in Sacramento, CA | Uplevel Cleaning" : `House Cleaning Services in ${locationData.name} | Uplevel Cleaning`}
          description={locationData.slug === 'sacramento' || locationData.slug === 'sacramento-area' ? "Professional house cleaning in Sacramento. Upfront pricing, vetted cleaners, 200% satisfaction guarantee. Book online in 60 seconds." : locationData.type === 'region' 
            ? `Book professional house cleaning, deep cleaning, move-out cleaning, and recurring cleaning services in ${locationData.name}. Get transparent pricing and easy online booking.`
            : `Uplevel Cleaning provides professional house cleaning, deep cleaning, move-out cleaning, and recurring cleaning services in ${locationData.name}. Get your exact price in 60 seconds.`}
          keywords={`house cleaning ${locationData.name}, maid service ${locationData.name}, home cleaning ${locationData.name}, deep cleaning ${locationData.name}, move out cleaning ${locationData.name}, recurring cleaning ${locationData.name}, uplevel cleaning ${locationData.name}`}
          url={`https://uplevelcleaning.com/locations/${locationData.slug}`}
          alternateUrl={SLUG_TO_ES[locationData.slug] ? `https://uplevelcleaning.com${SLUG_TO_ES[locationData.slug]}` : undefined}
          alternateLang={SLUG_TO_ES[locationData.slug] ? "es-US" : undefined}
          locationName={locationData.name}
          regionSlug={locationData.regionSlug}
          includeLocalBusiness={true}
          includeFAQ={true}
          breadcrumbs={[
            { name: "Home", url: "/" },
            { name: "Locations", url: "/locations" },
            { name: locationData.name, url: `/locations/${locationData.slug}` }
          ]}
          reviews={[
            { author: "Local Customer", rating: 5, reviewBody: `Uplevel Cleaning is the best cleaning service in ${locationData.name}. Professional, reliable, and spotless results every time.`, datePublished: "2026-03-01" },
            { author: "Happy Homeowner", rating: 5, reviewBody: `We've used Uplevel Cleaning for months in ${locationData.name}. They never disappoint. Highly recommend!`, datePublished: "2026-02-15" }
          ]}
        />
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <Hero />
          <TrustBar />
          
          {locationData.type === 'region' ? (
            <>
              <Suspense fallback={<div className="min-h-[200px] animate-pulse bg-muted/20" />}>
                <div className="content-auto">
                  <LazyHowItWorks />
                  <LazyServices />
                </div>
              </Suspense>

              <Suspense fallback={<div className="min-h-[200px]" />}>
                <div className="content-auto">
                  <LazyPricingCalculator />
                  <LazyWhyChooseUs />
                  <LazyBeforeYourCleaning />
                </div>
              </Suspense>

              <Suspense fallback={<div className="min-h-[200px]" />}>
                <div className="content-auto">
                  <LazyServiceAreaMap />
                  <LazyTestimonials />

                  <LazyComparison />
                </div>
              </Suspense>

              <Suspense fallback={<div className="min-h-[200px]" />}>
                <div className="content-auto">
                  <LazyFrequencyDiscounts />
                  <LazyChecklistVisualizer />
                  <LazyBeforeAfterGame />
                  <LazyCleanerSpotlight />
                </div>
              </Suspense>

              <Suspense fallback={<div className="min-h-[200px]" />}>
                <div className="content-auto">
                  <LazyNotForEveryone />
                  <LazyGuarantee />
                  <LazyReferFriend />
                  <LazyGiftCards />
                  <LazyTimeSavedCalculator />
                </div>
              </Suspense>
              
              <Suspense fallback={<div className="min-h-[200px]" />}>
                <div className="content-auto">
                  <LazyFAQ />
                  <LazyWhatToExpectTimeline />
                  <LazyInternalLinks />
                  <LazyFinalCTA />
                </div>
              </Suspense>
            </>
          ) : (
            <>
              <LocalSEOContent />
              <Testimonials />
              <WhyChooseUs />
              <Services />
              <FrequencyDiscounts />
              <PricingCalculator />
              <HowItWorks />

              <Comparison />
              <CleanerSpotlight />
              <NotForEveryone />
              <Guarantee />
              <ReferFriend />
              <GiftCards />
              <ServiceAreaMap />
              <div className="container py-12 grid md:grid-cols-2 gap-8">
                <WeatherRecommendation />
                <NeighborhoodMap />
              </div>
              <FAQ />
              <FinalCTA />
              <InternalLinks />
            </>
          )}

        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default LocationPage;
